import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Box from '../../spa/core-components/Box';
import { HeadingLink } from '../Link';
import { media } from '../../lib/styles';
import Inline from '../../spa/core-components/Inline';
import Select from '../Select/Select';
import Heading from '../../components/Heading';

type CollapsibleViewport = 'small' | 'medium' | 'normal' | 'large';

const ContentPanel = styled.div<{ isActive: boolean }>(({ isActive }) => {
    // for seo reasons we want all the content in the markup
    return css`
        display: ${isActive ? 'block' : 'none'};
        height: 100%;
    `;
});

interface Props {
    data: Item[];
    collapseOn?: CollapsibleViewport[];
    onChange?: (id: string) => void;
    onTabClick?: (element: Element) => void;
}

interface Item {
    id: string;
    title: string;
    content: React.ReactNode;
}

const NavElement = styled.div<{ hiddenOn?: CollapsibleViewport[] }>(({ hiddenOn = [] }) => {
    if (hiddenOn.length > 0) {
        return css`
            @media ${media(...hiddenOn)} {
                display: none;
            }
        `;
    }
    return css``;
});

export default function TabbedContent({ data, collapseOn = [], onChange, onTabClick }: Props) {
    const [activeId, setActiveId] = useState(data[0].id);
    const viewports: CollapsibleViewport[] = ['small', 'medium', 'normal', 'large'];
    const hiddenTabViewports = collapseOn;
    const hiddenSelectViewports = viewports.filter((x) => !hiddenTabViewports.includes(x));
    const scrollRef = useRef<HTMLDivElement>(null);

    return (
        <>
            <NavElement hiddenOn={hiddenSelectViewports}>
                <Select
                    onChange={(event) => {
                        //removes the synthetic event from the pool
                        //ref:https://duncanleung.com/fixing-react-warning-synthetic-events-in-setstate/
                        event.persist();
                        const value = event.target.value;
                        setActiveId(value);
                        onChange && onChange(value);
                    }}
                    value={activeId}
                    aria-label={__('Select content to view')}
                >
                    {data.map((item, index) => (
                        <option value={item.id} key={index} aria-selected={activeId === item.id}>
                            {item.title}
                        </option>
                    ))}
                </Select>
            </NavElement>

            <NavElement hiddenOn={hiddenTabViewports} ref={scrollRef}>
                <Inline space="space10">
                    {data.map((item, index) => (
                        <Heading type="h3" variations={['h5', 'no-margin']} key={index}>
                            <HeadingLink
                                href={`#${item.id}`}
                                key={item.id}
                                isActive={activeId === item.id}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveId(item.id);
                                    onChange && onChange(item.id);
                                    if (scrollRef.current && onTabClick) {
                                        onTabClick(scrollRef.current);
                                    }
                                }}
                            >
                                {item.title}
                            </HeadingLink>
                        </Heading>
                    ))}
                </Inline>
            </NavElement>

            <Box>
                {data.map((item) => (
                    <ContentPanel id={`${item.id}`} isActive={activeId === item.id} key={item.id}>
                        {item.content}
                    </ContentPanel>
                ))}
            </Box>
        </>
    );
}
